import { useMemo } from "react";
import * as d3 from "d3";
import { InteractionData } from "../../index";
import useWindowSize from "hooks/useWindowSize";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { formatMoney, formatMoneyHeatmap } from "helpers";


type RendererProps = {
  width: number;
  height: number;
  data: { x: string; y: string; value: number | null }[];
  setHoveredCell: (hoveredCell: InteractionData | null) => void;
  practitionerStats?: Map<string, { gaps: number; totalRevenue: number }>;
};

export const Renderer = ({
  width,
  height,
  data,
  setHoveredCell,
  practitionerStats,
}: RendererProps) => {
  const { width:windowWidth } = useWindowSize();
  const isMobile = windowWidth <= 480;

  const allXGroups = useMemo(() => [...new Set(data.map((d) => d.x))], [data]);

  const MARGIN = isMobile
    ? { top: 5, right: 5, bottom: 5, left: 5 }
    : { top: 140, right: 80, bottom: 30, left: 150 };

  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const allYGroups = useMemo(() => [...new Set(data.map((d) => d.y))], [data]);
  const [min = 0, max = 0] = d3.extent(data.map((d) => d.value ?? 0));

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([0, boundsWidth])
      .domain(allXGroups)
      .padding(-0.01)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, width]);

  const yScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([boundsHeight, 0])
      .domain(allYGroups)
      .padding(-0.01)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, height]);

  // Green scale for purchased cells
  var colorScale = d3
    .scaleSequential()
    .interpolator(d3.interpolateRgb("#C8E6C0", "#2E7D32"))
    .domain([min || 1, max]);

  // Build the rectangles
  const allShapes = data.map((d, i) => {
    const x = xScale(d.x);
    const y = yScale(d.y);
    const isGap = !d.value;

    const cellFill = isGap ? '#FDECEA' : colorScale(d.value!);
    const textColor = isGap ? '#B9261A' : '#1B5E20';
    const textWeight = isGap ? '700' : '400';
    const displayValue = isGap ? '$0' : formatMoneyHeatmap(d.value, isMobile);

    const cellData: InteractionData = {
      xLabel: d.x,
      yLabel: d.y,
      xPos: (x ?? 0) + xScale.bandwidth() * 0.6,
      yPos: (y ?? 0) + xScale.bandwidth() * 0.8,
      value: d.value ?? 0,
    };

    // On mobile: tap to select (no hover). On desktop: hover to preview.
    const interactionProps = isMobile
      ? { onClick: () => setHoveredCell(cellData) }
      : {
          onMouseEnter: () => setHoveredCell(cellData),
          onMouseLeave: () => setHoveredCell(null),
        };

    return(
     <g key={i}>
        <rect
          x={xScale(d.x)}
          y={yScale(d.y)}
          width={xScale.bandwidth()}
          height={yScale.bandwidth()}
          opacity={1}
          fill={cellFill}
          stroke={isGap ? '#E8B4AE' : '#A5D6A7'}
          strokeWidth={0.5}
          rx={2}
          cursor="pointer"
          {...interactionProps}
        />
        <text
          cursor="pointer"
          x={(xScale(d.x) ?? 0) + xScale.bandwidth()/2}
          y={(yScale(d.y) ?? 0 ) + yScale.bandwidth()/7 * 4}
          fontWeight={textWeight}
          fill={textColor}
          fontSize={12}
          textAnchor="middle"
          dominantBaseline="middle"
          pointerEvents="none"
        >
          {displayValue}
        </text>
     </g>
    );
  });

  // Wrap product name into max 2 lines, truncating with "..." if needed
  const wrapName = (name: string, maxChars: number): string[] => {
    if (name.length <= maxChars) return [name];
    const words = name.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      if (current && (current.length + 1 + word.length) > maxChars) {
        lines.push(current);
        current = word;
        if (lines.length === 2) break;
      } else {
        current = current ? current + ' ' + word : word;
      }
    }
    if (current && lines.length < 2) lines.push(current);
    // If there's still leftover text, truncate the last line
    const remaining = words.join(' ');
    const rendered = lines.join(' ');
    if (remaining.length > rendered.length) {
      lines[lines.length - 1] = lines[lines.length - 1].substring(0, maxChars - 3) + '...';
    }
    return lines;
  };

  const xLabels = allXGroups.map((name, i) => {
    const x = xScale(name);

    if (!x) {
      return null;
    }

    const lines = wrapName(name, 20);
    const lineHeight = 13;
    const anchorX = x + xScale.bandwidth() / 2;
    const anchorY = -10 - (lines.length - 1) * lineHeight;

    return (
      <text
        key={i}
        x={anchorX}
        y={anchorY}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={11}
        fill="black"
        transform={`rotate(-30, ${x + xScale.bandwidth() / 2}, -10)`}
      >
        {lines.map((line, li) => (
          <tspan key={li} x={anchorX} dy={li === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    );
  });

  const yLabels = allYGroups.map((name, i) => {
    const y = yScale(name);

    if (!y) {
      return null;
    }

    const stats = practitionerStats?.get(name);
    const gapLabel = stats ? `${stats.gaps} gap${stats.gaps !== 1 ? 's' : ''}` : '';

    return (
      <g key={i}>
        {/* Practitioner name */}
        <text
          y={y + yScale.bandwidth() / 2 - (stats ? 7 : 0)}
          x={-10}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={12}
          fill="black"
        >
          {name.length > 20 ? name.substring(0,20) + '...' : name}
        </text>
        {/* Gap count subtitle */}
        {stats && (
          <text
            y={y + yScale.bandwidth() / 2 + 9}
            x={-10}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={10}
            fill={stats.gaps > 0 ? '#B9261A' : '#479D7F'}
            fontWeight={500}
          >
            {gapLabel}
          </text>
        )}
      </g>
    );
  });


  return (
    <svg width={width} height={height}>
      <g
        width={boundsWidth}
        height={boundsHeight}
        transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
      >
        {allShapes}
        {!isMobile &&
        <>
        {xLabels}
        {yLabels}
        </>
        }

      </g>
    </svg>
  );
};
